import React from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function AiNarrativePanel({ narrative, edited, onSave, onRegenerate, fullHeight }) {
  const [draft, setDraft] = React.useState(narrative || "");
  const [saving, setSaving] = React.useState(false);
  const [open, setOpen] = React.useState(true);

  React.useEffect(() => { setDraft(narrative || ""); }, [narrative]);

  const dirty = (draft || "") !== (narrative || "");
  const hasContent = (narrative || "").trim().length > 0;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(narrative || "");
      toast.success("Texto copiado al portapapeles");
    } catch (e) {
      console.warn("clipboard.writeText failed:", e);
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      className={`bg-white/[0.04] border border-white/10 rounded-[14px] flex flex-col ${fullHeight ? "h-full" : ""}`}
      style={fullHeight ? undefined : { maxHeight: open ? "40vh" : "auto" }}
      data-testid="ai-narrative-panel"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="px-4 py-3 border-b border-white/10 text-sm font-medium text-white/85 flex items-center gap-2 hover:bg-white/[0.02] transition-colors"
        title={open ? "Contraer" : "Expandir"}
        data-testid="ai-narrative-toggle"
      >
        <Sparkles className="h-3.5 w-3.5 text-[#D85A30]" />
        <span>Causas y circunstancias · para Ekspertium</span>
        {edited && <span className="text-[9px] text-[#5BCBA0] font-mono opacity-80" title="Editado manualmente">·editado</span>}
        <span className="ml-auto text-[10px] text-white/45">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="flex-1 overflow-y-auto vp-scroll p-3 space-y-2">
          {!hasContent && !dirty ? (
            <div className="text-xs text-white/55 text-center py-3">
              A medida que la conversación avance, aquí se generará un texto narrativo listo para enviar al ERP.<br />
              <span className="text-[10px] text-white/40">Puedes editarlo libremente antes de finalizar.</span>
            </div>
          ) : null}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full bg-white/5 text-white text-[12px] leading-relaxed p-2 rounded-md border border-white/10 focus:border-[#D85A30] outline-none resize-none vp-scroll"
            rows={8}
            placeholder="(El texto narrativo aparecerá aquí cuando haya transcripción suficiente)"
            data-testid="ai-narrative-textarea"
          />
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={onRegenerate}
              disabled={saving}
              className="text-[11px] text-white/60 hover:text-white/95 disabled:opacity-50 px-2 py-1 rounded transition-colors"
              title="Regenerar desde la IA (descarta tus ediciones)"
              data-testid="ai-narrative-regenerate-btn"
            >Regenerar desde IA</button>
            <button
              onClick={handleCopy}
              disabled={!hasContent}
              className="text-[11px] text-white/60 hover:text-white/95 disabled:opacity-40 px-2 py-1 rounded transition-colors"
              data-testid="ai-narrative-copy-btn"
            >Copiar</button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="text-[11px] text-white bg-[#D85A30]/90 hover:bg-[#D85A30] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 rounded transition-colors flex items-center gap-1"
              data-testid="ai-narrative-save-btn"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
